"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Loader2, CheckCircle2, Lock } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@/hooks/use-auth"
import { checkEnrollment, createEnrollment } from "@/lib/enrollment"
import { motion } from "framer-motion"

interface Course {
  id: string
  title: string
  fees?: number
  type?: string
  imageUrl?: string
  instructor?: string
  duration?: string
  discountedPrice?: number
  originalPrice?: number
}

interface EnrollButtonProps {
  course: Course
  className?: string
  size?: "sm" | "md" | "lg"
}

export default function EnrollButton({ course, className = "", size = "lg" }: EnrollButtonProps) {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [enrolled, setEnrolled] = useState(false)
  const [checkingEnrollment, setCheckingEnrollment] = useState(true)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    // Wait for auth to initialize before checking enrollment
    if (authLoading) return

    if (!user) { 
      setCheckingEnrollment(false)
      return 
    }

    checkEnrollment(user.uid, course.id).then(result => {
      setEnrolled(result)
      setCheckingEnrollment(false)
    })
  }, [user, authLoading, course.id])

  const handleEnroll = async () => {
    if (!user) {
      toast.info("Please sign in to enroll in this course.")
      return navigate("/login")
    }
    setProcessing(true)
    try {
      await createEnrollment({
        userId: user.uid,
        courseId: course.id,
        courseTitle: course.title,
        courseFees: 0,
        courseType: "free",
        courseImageUrl: course.imageUrl,
        instructor: course.instructor,
        duration: course.duration,
        enrolledAt: null,
        status: "active",
        paymentMethod: "free",
        paymentStatus: "free",
      })
      setEnrolled(true)
      toast.success("🎉 Enrolled successfully! Start learning now.")
    } catch (err) {
      toast.error("Enrollment failed. Please try again.")
    } finally {
      setProcessing(false)
    }
  }

  const sizeClass = {
    sm: "px-5 py-2.5 text-[9px]",
    md: "px-7 py-3 text-[10px]",
    lg: "px-8 py-4 text-[10px] sm:py-5 sm:px-10",
  }[size]

  // Show checking state if auth is loading OR if we're fetching enrollment status
  if (authLoading || checkingEnrollment) {
    return (
      <div className={`flex items-center justify-center gap-2 bg-slate-100 text-slate-400 font-bold uppercase tracking-widest ${sizeClass} ${className}`}>
        <Loader2 size={14} className="animate-spin" />
        Checking...
      </div>
    )
  }

  if (enrolled) {
    return (
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className={`flex items-center justify-center gap-2 bg-emerald-950 text-emerald-400 border border-emerald-800 font-bold uppercase tracking-widest ${sizeClass} ${className}`}
      >
        <CheckCircle2 size={16} />
        Enrolled — Access Active
      </motion.div>
    )
  }

  if (!user) {
    return (
      <button
        onClick={() => {
          toast.info("Please sign in to enroll in this course.")
          navigate("/login")
        }}
        className={`flex items-center justify-center gap-2 bg-slate-800 hover:bg-[#b48c36] text-white font-bold uppercase tracking-widest transition-colors ${sizeClass} ${className}`}
      >
        <Lock size={14} />
        Sign In to Enroll
      </button>
    )
  }

  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      onClick={handleEnroll}
      disabled={processing}
      className={`flex items-center justify-center gap-2 bg-[#b48c36] hover:bg-white hover:text-slate-950 text-white font-bold uppercase tracking-[0.15em] transition-all shadow-lg shadow-[#b48c36]/20 disabled:opacity-60 ${sizeClass} ${className}`}
    >
      {processing ? (
        <><Loader2 size={14} className="animate-spin" /> Processing...</>
      ) : (
        <>Enroll Now — Free</>
      )}
    </motion.button>
  )
}
